/**
 * QuoteViewsMenu — Quotations' part of the shell flyout (docs/audits/PILOT-QUOTES-SHELL.md
 * §4.1, S3). One component, rendered in three places: nested under Quotations in the Sales
 * flyout (rail), inline under the Quotations row (expanded sidebar), and behind the list
 * header's "View: … ▾" button.
 *
 *   Views    the saved-view set behind the retired chips, plus My quotes and Stale, with
 *            the counts from the views block of /api/quotations/stats/filter-counts. The
 *            request carries no q: the flyout counts every quote in each view, whatever the
 *            list is searching for. It goes through the api cache, so it follows S1b's rule:
 *            cached until a list reload (a status change, a delete) invalidates it.
 *   Recents  the last quotes this user opened: quotes.recents in their document (S4b).
 *   New quote
 *
 * Every entry is a link (a menuitem): choosing one is a URL change; the menu holds no
 * state of its own. Colours are status tokens only.
 */
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { cachedFetch } from '../../services/apiCache';
import { quoteViewHref, useQuoteListDefaults } from './useQuoteRoute';
import { useRecentQuotes, rememberQuoteLabel } from './recentQuotes';
import { usePreferences } from '../../contexts/PreferencesContext';
import '../layout/Flyout.css';
import './QuoteViewsMenu.css';

export const VIEWS_COUNTS_PATH = '/api/quotations/stats/filter-counts';

/** The views, in the doc's order (§4.1); `tone` is the --status-<tone> family of its dot. */
export const QUOTE_VIEW_ENTRIES = [
  { id: 'all', label: 'All', tone: 'expired' },
  { id: 'draft', label: 'Draft', tone: 'draft' },
  { id: 'sent', label: 'Sent', tone: 'sent' },
  { id: 'pending_approval', label: 'Pending approval', tone: 'pending' },
  { id: 'won', label: 'Won', tone: 'won' },
  { id: 'lost', label: 'Lost', tone: 'lost' },
  { id: 'expiring', label: 'Expiring soon', tone: 'warning' },
  { id: 'high_value', label: 'High value', tone: 'success' },
  { id: 'recent', label: 'Recent', tone: 'info' },
  { id: 'no_customer', label: 'No customer', tone: 'error' },
  { id: 'mine', label: 'My quotes', tone: 'viewed' },
  { id: 'stale', label: 'Stale', tone: 'negotiating' }
];

export const quoteViewLabel = (view) =>
  (QUOTE_VIEW_ENTRIES.find((v) => v.id === view) || QUOTE_VIEW_ENTRIES[0]).label;

/** The views block, read once per mount through the api cache. The palette's Views read it too (S6). */
export function useViewCounts() {
  const [counts, setCounts] = useState(null);
  useEffect(() => {
    let alive = true;
    cachedFetch(VIEWS_COUNTS_PATH)
      .then((body) => { if (alive && body?.views) setCounts(body.views); })
      .catch(() => { /* no counts: the views still work as links */ });
    return () => { alive = false; };
  }, []);
  return counts;
}

/**
 * A recent quote's label on a device that has not opened it yet: fetched once (through the
 * api cache) and kept in the user's label cache. Until it answers the entry reads "Quote <id>".
 */
function useRecentLabels(recents, enabled) {
  const { userId } = usePreferences('quotes');
  const [fetched, setFetched] = useState({});
  const missing = enabled ? recents.filter((r) => !r.quote_number && !fetched[r.id]).map((r) => r.id) : [];
  const missingKey = missing.join(',');
  useEffect(() => {
    if (!missingKey) return undefined;
    let alive = true;
    missingKey.split(',').map(Number).forEach((id) => {
      cachedFetch(`/api/quotes/${id}`)
        .then((q) => {
          if (!alive || !q || !q.id) return;
          rememberQuoteLabel(userId, q);
          setFetched((f) => ({ ...f, [id]: { quote_number: q.quote_number || q.quotation_number, customer_name: q.customer_name } }));
        })
        .catch(() => { /* deleted or unreachable: it keeps its "Quote <id>" label */ });
    });
    return () => { alive = false; };
  }, [missingKey, userId]);
  return recents.map((r) => (r.quote_number ? r : { ...r, ...(fetched[r.id] || {}) }));
}

/**
 * @param {object} props
 * @param {string} [props.activeView]   the view on screen, marked as current
 * @param {boolean} [props.keepListKeys] keep how the list is shown — sort, direction, layout
 *   (the list header's menu); a view still replaces the search and filters, as a chip did.
 *   The sidebar's views are the bare views.
 * @param {boolean} [props.inline]      views only (the expanded sidebar's child list)
 */
export default function QuoteViewsMenu({ activeView = null, keepListKeys = false, inline = false }) {
  const counts = useViewCounts();
  const location = useLocation();
  const currentSearch = keepListKeys ? location.search : '';
  const defaults = useQuoteListDefaults();
  const { recents: stored } = useRecentQuotes();
  const labelled = useRecentLabels(stored, !inline);
  const recents = inline ? [] : labelled;

  return (
    <div className={`quote-views-menu${inline ? ' quote-views-menu-inline' : ''}`}>
      <div role="group" aria-label="Quotation views">
        {!inline && <div className="shell-flyout-heading" aria-hidden="true">Quotation views</div>}
        <ul className="shell-flyout-list" role="none">
          {QUOTE_VIEW_ENTRIES.map((v) => {
            const count = counts && Number.isFinite(counts[v.id]) ? counts[v.id] : null;
            const current = activeView === v.id;
            return (
              <li key={v.id} role="none">
                <Link
                  to={quoteViewHref(v.id, currentSearch, defaults)}
                  role="menuitem"
                  aria-label={count === null ? v.label : `${v.label} (${count})`}
                  aria-current={current ? 'page' : undefined}
                  className={`shell-flyout-item quote-views-item${current ? ' active' : ''}`}
                  style={{ '--quote-view-tone': `var(--status-${v.tone})` }}
                >
                  <span className="quote-views-dot" aria-hidden="true" />
                  <span className="quote-views-label">{v.label}</span>
                  <span className="quote-views-count" aria-hidden="true">{count === null ? '' : count}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>

      {recents.length > 0 && (
        <div role="group" aria-label="Recent quotes">
          <div className="shell-flyout-divider" />
          <div className="shell-flyout-heading" aria-hidden="true">Recent</div>
          <ul className="shell-flyout-list" role="none">
            {recents.map((r) => (
              <li key={r.id} role="none">
                <Link to={`/quotes/${r.id}`} role="menuitem" className="shell-flyout-item quote-views-recent">
                  <span className="quote-views-label">{r.quote_number || `Quote ${r.id}`}</span>
                  {r.customer_name && <span className="quote-views-sub">{r.customer_name}</span>}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!inline && (
        <>
          <div className="shell-flyout-divider" />
          <ul className="shell-flyout-list" role="none">
            <li role="none">
              <Link to="/quotes/new" role="menuitem" className="shell-flyout-item">
                <Plus aria-hidden="true" />
                <span className="quote-views-label">New quote</span>
              </Link>
            </li>
          </ul>
        </>
      )}
    </div>
  );
}
